import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import * as time from 'library/time';
import settings from 'settings';

// Data Shapes
// CR-1899
const {
  bool, number, shape, string, object, instanceOf
} = PropTypes;

export const dataShapes = {
  local_items: shape({
    activity: shape({
      active: bool.isRequired,
      color: string.isRequired,
      duration: string.isRequired,
      id: number.isRequired,
      name: string.isRequired
    }).isRequired,
    color: string.isRequired,
    end_time: instanceOf(moment),
    group: number.isRequired,
    id: number.isRequired,
    itemProps: shape({
      style: shape({
        backgroundColor: string.isRequired
      }).isRequired
    }).isRequired,
    notes: string.isRequired,
    start_time: object.isRequired,
    title: string.isRequired,
    user: shape({
      active: bool.isRequired,
      first_name: string.isRequired,
      id: number.isRequired,
      last_name: string.isRequired,
      title: string.isRequired,
      uid: number.isRequired
    }).isRequired,
    user_id: number.isRequired
  }).isRequired
};

export const isTravelItem = item => item.id.toString()
  .startsWith('t-');
export const isLeaveItem = item => item.id.toString()
  .startsWith('l-');

export const getPublishedRosters = (list = {}) => {
  const items = list.results || [];
  const publishedRosters = [];

  items.forEach((roster) => {
    const versions = roster.versions || [];
    const publishedVersions = versions.filter(version => version.is_published);

    publishedVersions.forEach((version) => {
      publishedRosters.push({
        versionId: version.id || -1,
        name: roster.name,
        published: version.is_published
      });
    });
  });

  return _.sortBy(publishedRosters, rosterVersion => rosterVersion.name);
};

export const combineRosterItems = (groups = [], serverItems = [], travelItems = [], leaveItems = []) => {
  const list = _.unionBy(serverItems, travelItems, leaveItems, 'id');

  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    // Check start/end times are set as a moment object
    if (!moment.isMoment(item.start_time)) {
      item.start_time = moment(item.start_time);
    }
    if (!moment.isMoment(item.end_time)) {
      item.end_time = moment(item.end_time);
    }

    // Setup the association between the user and the item.
    if (typeof item.user_id === 'number') {
      const group = groups.find(g => g.uid === item.user_id);
      item.group = group ? group.id : null;
    }
  }

  // Remove any activities which are not associated to a user
  return list.filter(item => item.group !== null);
};

export const getEffectiveCycleStart = (cycle, visStart) => {
  // Optimization for cycle start
  const cycleStart = moment(cycle.start_datetime);
  const { legs } = cycle.availability_cycle;
  const msDiff = visStart.diff(cycleStart);
  if (msDiff < 0) {
    return cycleStart;
  }

  // Get duration for 1 complete cycle:
  const oneCycleDuration = moment.duration(0);
  legs.forEach(({ leg }) => {
    const legDuration = time.djangoDurationToMoment(leg.duration);
    oneCycleDuration.add(legDuration);
  });

  const completeBlocks = Math.trunc(msDiff / oneCycleDuration.asMilliseconds());
  const completeMs = oneCycleDuration.asMilliseconds() * completeBlocks;

  cycleStart.add(completeMs, 'milliseconds');
  return cycleStart;
};

export const getAvailabilityLayers = (rawLayers, userGroups, visibleStart, visibleEnd) => {
  // Shape expected by rct9k:
  // {start: <MOMENT>, end: <MOMENT>, style: <OBJ>, rowNumber: <INT> }
  const mappedLayer = [];
  rawLayers.forEach((cycle) => {
    const cycleStart = getEffectiveCycleStart(cycle, visibleStart);
    const cycleEnd = cycle.end_datetime ? moment(cycle.end_datetime) : null;

    const group = _.find(userGroups, g => g.uid === cycle.user);
    const groupId = group ? group.id : null;

    const legs = _.sortBy(cycle.availability_cycle.legs, leg => leg.order);
    const legsInCycle = legs.length;
    const upToDate = cycleStart.clone();
    let currentLegIndex = 0;
    if (legs.length > 0) {
      while (upToDate.isSameOrBefore(cycleEnd) || upToDate.isSameOrBefore(visibleEnd)) {
        const { leg } = legs[currentLegIndex];
        const duration = time.djangoDurationToMoment(leg.duration);
        const itemStart = upToDate.clone();
        upToDate.add(duration);
        const maxEnd = [upToDate, visibleEnd];
        // Cycle may not have end date
        if (cycleEnd) {
          maxEnd.push(cycleEnd);
        }
        const itemEnd = moment.min(...maxEnd)
          .clone();
        const item = {
          start: itemStart,
          end: itemEnd,
          style: {
            backgroundColor: leg.type.color,
            opacity: settings.APP_CONFIG.availabilityOpacity
          },
          rowNumber: groupId
        };
        mappedLayer.push(item);
        currentLegIndex = (currentLegIndex + 1) % legsInCycle;
      }
    }
  });
  return mappedLayer;
};

export const getRosters = (list = {}) => {
  const items = list.results || [];
  const rosters = [];

  items.forEach((roster) => {
    const versions = roster.versions || [];
    const unPublishedVersions = versions.filter(version => !version.is_published);

    rosters.push({
      name: roster.name,
      id: roster.id,
      unPublishedRosterVersions: unPublishedVersions || []
    });
  });
  return rosters;
};

export const getRosterVersionsBasedOnRosterSelected = (list = {}) => {
  const items = list.results || [];
  const rosters = [];

  items.forEach((roster) => {
    const versions = roster.versions || [];
    const unPublishedVersions = versions.filter(version => !version.is_published);

    rosters.push({
      name: roster.name,
      id: roster.id,
      unPublishedRosterVersions: unPublishedVersions || []
    });
  });
  return rosters;
};

